import { Link, useLocation, useParams } from 'react-router-dom';

import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import useGetOrganisation from '@/api/queries/useGetOrganisation';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { SettingsNav, SettingsNavGroup, SettingsNavItem, SettingsWrapper } from '@/new/components/settings-nav';
import { cn } from '@/new/lib/utils';

interface AdminOrganisationTab {
  id: string;
  title: string;
  iconClass: string;
  permission: Permission;
}

const tabs: AdminOrganisationTab[] = [
  {
    id: 'configuration',
    title: 'Configuration',
    iconClass: 'lucide--sliders-horizontal',
    permission: Permission.PlatformManageOrganisations,
  },
  {
    id: 'billing',
    title: 'Billing',
    iconClass: 'lucide--circle-dollar-sign',
    permission: Permission.PlatformManageBilling,
  },
  {
    id: 'general',
    title: 'General',
    iconClass: 'lucide--settings',
    permission: Permission.PlatformManageOrganisations,
  },
  { id: 'users', title: 'Manage Users', iconClass: 'lucide--users-round', permission: Permission.PlatformManageUsers },
  {
    id: 'feedback',
    title: 'Feedback',
    iconClass: 'lucide--message-square-more',
    permission: Permission.PlatformViewFeedback,
  },
  { id: 'runs', title: 'Runs', iconClass: 'lucide--zap', permission: Permission.PlatformManageRuns },
];

export function AdminOrganisationLayout({ children }: { children: React.ReactNode }) {
  const { organisationId } = useParams();
  const location = useLocation();
  const { hasPermission } = useHasPermission();
  const { data: organisation } = useGetOrganisation(organisationId);

  if (!organisationId) return null;

  const lastSegment = location.pathname.split('/').filter(Boolean).at(-1);
  const activeTab = lastSegment === organisationId ? 'configuration' : lastSegment;
  const activeTitle = tabs.find((tab) => tab.id === activeTab)?.title;
  const isTablePage = activeTab === 'users' || activeTab === 'feedback' || activeTab === 'runs';

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb
        items={[
          { href: '/admin/organisations', label: 'Organisations', active: false },
          {
            href: `/admin/organisations/${organisationId}`,
            label: organisation?.name ?? 'Organisation',
            active: !activeTitle || activeTab === 'configuration',
          },
          ...(activeTitle && activeTab !== 'configuration'
            ? [{ href: location.pathname, label: activeTitle, active: true }]
            : []),
        ]}
      />

      <SettingsWrapper>
        <SettingsNav>
          <div className="px-2 pt-1">
            <p className="text-[13px] text-gray-500">Managing settings for </p>
            <p className="text-xl font-medium">{organisation?.name}</p>
          </div>
          <SettingsNavGroup>
            {tabs.map((tab) =>
              hasPermission(tab.permission) ? (
                <SettingsNavItem key={tab.id} asChild active={activeTab === tab.id}>
                  <Link
                    to={
                      tab.id === 'configuration'
                        ? `/admin/organisations/${organisationId}`
                        : `/admin/organisations/${organisationId}/${tab.id}`
                    }
                  >
                    <Icon className={tab.iconClass} />
                    <span>{tab.title}</span>
                  </Link>
                </SettingsNavItem>
              ) : null,
            )}
          </SettingsNavGroup>
        </SettingsNav>

        <div
          className={cn(
            'min-h-0 min-w-0 flex-1',
            isTablePage ? 'flex flex-col overflow-hidden pt-7' : 'overflow-auto px-8 pt-7 pb-8',
          )}
        >
          {children}
        </div>
      </SettingsWrapper>
    </div>
  );
}
